import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  BarChart3, ArrowLeft, Download, TrendingUp, TrendingDown,
  Calendar, DollarSign
} from 'lucide-react';

export default function TributarioRelatorios() {
  const [loading, setLoading] = useState(true);
  const [resumoAnual, setResumoAnual] = useState(null);
  const [comparativo, setComparativo] = useState(null);
  const [filtros, setFiltros] = useState({
    ano: new Date().getFullYear(),
    ano_comparacao: new Date().getFullYear() - 1
  });
  
  const navigate = useNavigate();

  useEffect(() => {
    loadResumo();
  }, [filtros.ano]);

  const loadResumo = async () => {
    setLoading(true);
    try {
      const response = await api.get('/tributario/relatorios/resumo-anual', {
        params: { ano: filtros.ano }
      });
      setResumoAnual(response.data);
    } catch (error) {
      console.error('Erro ao carregar resumo:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadComparativo = async () => {
    try {
      const response = await api.get('/tributario/relatorios/comparativo', {
        params: { ano1: filtros.ano_comparacao, ano2: filtros.ano }
      });
      setComparativo(response.data);
    } catch (error) {
      toast.error('Erro ao carregar comparativo');
    }
  };

  const exportarCSV = () => {
    if (!resumoAnual?.mensal?.length) {
      toast.error('Nenhum dado para exportar');
      return;
    }

    const headers = ['Mês', 'Receita', 'PIS', 'COFINS', 'ISS', 'IRPJ', 'CSLL', 'Total'];
    const rows = resumoAnual.mensal.map(m => [
      ['', 'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'][m.mes],
      m.receita_faturada?.toFixed(2) || '0.00',
      m.pis?.toFixed(2) || '0.00',
      m.cofins?.toFixed(2) || '0.00',
      m.iss?.toFixed(2) || '0.00',
      m.irpj?.toFixed(2) || '0.00',
      m.csll?.toFixed(2) || '0.00',
      m.total?.toFixed(2) || '0.00'
    ]);

    const csv = [
      `Relatório Tributário - ${filtros.ano}`,
      '',
      headers.join(';'),
      ...rows.map(r => r.join(';')),
      '',
      `Total Receita;${resumoAnual.totais?.receita_faturada?.toFixed(2)}`,
      `Total Tributos;${resumoAnual.totais?.total_tributos?.toFixed(2)}`,
      `Carga Média;${resumoAnual.totais?.carga_media?.toFixed(2)}%`
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relatorio_tributario_${filtros.ano}.csv`;
    link.click();
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-[400px]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="tributario-relatorios">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/tributario')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6" />
            Relatórios Fiscais
          </h1>
          <p className="text-muted-foreground">Resumos e comparativos tributários</p>
        </div>
        <div className="flex gap-2">
          <Select 
            value={String(filtros.ano)} 
            onValueChange={(v) => setFiltros(prev => ({ ...prev, ano: parseInt(v) }))}
          >
            <SelectTrigger className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026].map(ano => (
                <SelectItem key={ano} value={String(ano)}>{ano}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={exportarCSV}>
            <Download className="w-4 h-4 mr-2" />
            Exportar
          </Button>
        </div>
      </div>

      {/* Cards de Totais */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Receita Total</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              R$ {(resumoAnual?.totais?.receita_faturada || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Recebido</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-green-600">
              R$ {(resumoAnual?.totais?.receita_recebida || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Total Tributos</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-purple-600">
              R$ {(resumoAnual?.totais?.total_tributos || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Carga Média</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {(resumoAnual?.totais?.carga_media || 0).toFixed(2)}%
            </p>
            <p className="text-xs text-muted-foreground">{resumoAnual?.meses_apurados || 0} meses apurados</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabela Mensal */}
      <Card>
        <CardHeader>
          <CardTitle>Apuração Mensal - {filtros.ano}</CardTitle>
        </CardHeader>
        <CardContent>
          {resumoAnual?.mensal?.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Mês</TableHead>
                    <TableHead className="text-right">Receita</TableHead>
                    <TableHead className="text-right">PIS</TableHead>
                    <TableHead className="text-right">COFINS</TableHead>
                    <TableHead className="text-right">ISS</TableHead>
                    <TableHead className="text-right">IRPJ</TableHead>
                    <TableHead className="text-right">CSLL</TableHead>
                    <TableHead className="text-right font-bold">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {resumoAnual.mensal.map((m) => (
                    <TableRow key={m.mes}>
                      <TableCell className="font-medium">
                        {['', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 
                          'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'][m.mes]}
                      </TableCell>
                      <TableCell className="text-right">
                        R$ {(m.receita_faturada || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right">
                        R$ {(m.pis || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right">
                        R$ {(m.cofins || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right">
                        R$ {(m.iss || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right">
                        R$ {(m.irpj || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right">
                        R$ {(m.csll || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right font-bold text-purple-600">
                        R$ {(m.total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              Nenhuma apuração encontrada para este ano.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Comparativo */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Comparativo Anual</span>
            <div className="flex items-center gap-2">
              <Select 
                value={String(filtros.ano_comparacao)} 
                onValueChange={(v) => setFiltros(prev => ({ ...prev, ano_comparacao: parseInt(v) }))}
              >
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2023, 2024, 2025].map(ano => (
                    <SelectItem key={ano} value={String(ano)}>{ano}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span>vs</span>
              <span className="font-bold">{filtros.ano}</span>
              <Button variant="outline" size="sm" onClick={loadComparativo}>
                Comparar
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {comparativo ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h4 className="font-medium">{comparativo.periodo1.ano}</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Receita:</span>
                    <span className="font-medium">
                      R$ {(comparativo.periodo1.receita || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Tributos:</span>
                    <span className="font-medium">
                      R$ {(comparativo.periodo1.tributos || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-medium">{comparativo.periodo2.ano}</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Receita:</span>
                    <span className="font-medium">
                      R$ {(comparativo.periodo2.receita || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Tributos:</span>
                    <span className="font-medium">
                      R$ {(comparativo.periodo2.tributos || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>

              <div className="col-span-2 p-4 bg-muted rounded-lg">
                <h4 className="font-medium mb-2">Variações</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <span>Receita:</span>
                    <span className={`font-bold flex items-center ${comparativo.variacoes.receita_percentual >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {comparativo.variacoes.receita_percentual >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                      {comparativo.variacoes.receita_percentual.toFixed(2)}%
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>Tributos:</span>
                    <span className={`font-bold flex items-center ${comparativo.variacoes.tributos_percentual >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {comparativo.variacoes.tributos_percentual >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                      {comparativo.variacoes.tributos_percentual.toFixed(2)}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              Selecione os anos e clique em "Comparar" para ver o comparativo.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
